import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { Container } from '../components/_atoms';
import { ROUTES } from '../constants';
import { SchoolDTO } from '../DTO/SchoolDTO';
import LastEdited from '../components/lastEdited';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { FormGroup, Typography, styled } from '@mui/material';
import { APIService } from '../services/apiService';
import { CountryDTO } from '../DTO/CountryDTO';
import { useSchools, useUpdateSchools } from '../contexts';
import { useIsDirty } from '../hooks/isDirty';

const StyledButton = styled(Button)<{ component?: React.ElementType; to?: string }>({
	margin: '12px 6px 18px 0',
});

const StyledTextField = styled(TextField)({
	marginBottom: '12px',
	marginRight: '12px',
	display: 'block',
});


const api = new APIService();

const School = () => {
	const params = useParams<{ id?: string }>();
	const navigate = useNavigate();
	const { saveSchool } = useUpdateSchools();
	const schools = useSchools();
	const schoolId: number = params.id ? parseInt(params.id) : 0;

	const [school, setSchool] = useState<SchoolDTO>(
		schoolId > 0
			? schools?.find((s) => s.id == schoolId) || new SchoolDTO()
			: new SchoolDTO()
	);
	const [error, setError] = useState('');
	const [countries, setCountries] = useState<null | CountryDTO[]>(null);
	const [isDirty, updateCleanValue] = useIsDirty(school);

	useEffect(() => {
		if (countries === null) {
			api.getCountries().then((data) => setCountries(data));
		}
	});

	useEffect(() => {
		const parsedId = params.id && parseInt(params.id);
		if (parsedId && parsedId > 0 && schools) {
			const activeSchool = schools?.find((s) => s.id == parsedId);
			if (activeSchool) {
				updateCleanValue(activeSchool);
				setSchool(activeSchool);
			}
		}
	}, [params?.id, schools]);

	const handleSubmit = (e: React.FormEvent) => {
		saveSchool(school)
			.then((newSchool) => {
				setSchool(newSchool);
				updateCleanValue(newSchool);
				if (school.id === 0) {
					navigate(`${ROUTES.SCHOOL}/${newSchool.id}`, { replace: true });
				}
			})
			.catch((ex) => {
				setError(ex.response.data.message);
			});
		e.preventDefault();
	};

	const setSchoolName = (name: string) => {
		setSchool((prevSchool) => ({ ...prevSchool, name }));
	};

	return !school ? (
		<h1>Could not find school</h1>
	) : (
		<Container>
			<Typography
				gutterBottom
				style={{ marginTop: '5px' }}
				variant="h4"
				align="center"
			>
				{schoolId === 0 ? 'Create New ' : ''}University
			</Typography>
			<StyledButton
				variant="contained"
				color="secondary"
				size="small"
				component={Link}
				to={`${ROUTES.COURSES}/${school.id}`}
			>
				Courses
			</StyledButton>
			<form onSubmit={handleSubmit}>
				<FormGroup row>
					<StyledTextField
						type="text"
						label="Name"
						variant="outlined"
						value={school.name}
						onChange={(e) => setSchoolName(e.target.value)}
					/>
					{countries && countries.length > 0 ? (
						<Autocomplete
							style={{ width: '200px' }}
							value={school?.country?.name}
							onChange={(event, newValue) => {
								if (!countries) return;
								const country = countries.find(
									(c) => c.name == newValue
								);
								setSchool(
									(prevSchool) =>
										({
											...prevSchool,
											country,
											countryId: country?.id,
										} as SchoolDTO)
								);
							}}
							options={countries.map((option) => option.name)}
							renderInput={(params) => (
								<StyledTextField
									{...params}
									label="Country"
									variant="outlined"
								/>
							)}
						/>
					) : null}
				</FormGroup>
				<StyledButton
					size="small"
					variant="contained"
					color="primary"
					type="submit"
					disabled={!isDirty}
				>
					Save
				</StyledButton>
			</form>

			<LastEdited data={school}></LastEdited>
			<p>{error}</p>
		</Container>
	);
};

export default School;
